import { useEffect, useState } from 'react';
import {
  fetchDriverStandings,
  fetchDriverStandingsAtRound,
  fetchConstructorStandings,
  fetchConstructorStandingsAtRound,
  driverDisplayName,
} from '../../services/jolpicaClient.js';
import { teamColors } from '../../theme/tokens.js';
import { parsePosition, orderStandings } from '../../utils/standings.js';
import Skeleton from '../common/Skeleton.jsx';
import Icon from '../common/Icon.jsx';
import StandingsHistory from './StandingsHistory.jsx';

/**
 * StandingsView — drivers' and constructors' championships for one season.
 *
 * Three things beyond a plain table:
 *   - a round selector, so the table can be read as it stood after any race
 *     rather than only at the end of the season
 *   - expandable rows, opening a race-by-race history for that entrant
 *     (constructors show both cars per race)
 *   - position deltas against the previous round
 *
 * Mounted with key={year} by ResultsScreen — a season change remounts this and
 * resets loading/selection state from the initialisers, instead of a
 * synchronous setState in the effect body queueing an extra render.
 */
export default function StandingsView({ year }) {
  const [mode, setMode] = useState('drivers');
  /** null = "as it finished" / latest available round. */
  const [round, setRound] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();

    (async () => {
      try {
        const [dResult, tResult] = await Promise.all([
          round === null
            ? fetchDriverStandings(year, { signal: ctrl.signal })
            : fetchDriverStandingsAtRound(year, round, { signal: ctrl.signal }).then((standings) => ({
                round,
                standings,
              })),
          round === null
            ? fetchConstructorStandings(year, { signal: ctrl.signal })
            : fetchConstructorStandingsAtRound(year, round, { signal: ctrl.signal }).then((standings) => ({
                round,
                standings,
              })),
        ]);
        if (ctrl.signal.aborted) return;

        const shownRound = dResult.round ?? round;

        // Deltas need the table one round earlier. Non-critical: a failure here
        // just means no arrows.
        let previous = {};
        if (shownRound && shownRound > 1) {
          try {
            const prev = await fetchDriverStandingsAtRound(year, shownRound - 1, { signal: ctrl.signal });
            prev.forEach((entry) => {
              const id = entry.Driver?.driverId;
              if (id) previous[id] = parseInt(entry.position, 10);
            });
          } catch { /* non-critical */ }
        }

        if (ctrl.signal.aborted) return;
        setData((current) => ({
          requestedRound: round,
          shownRound,
          // The full-season round count comes from the unfiltered load and is
          // preserved afterwards — a request for round 3 reports round 3 as its
          // latest, which would otherwise shrink the selector to three entries.
          maxRound: round === null ? shownRound : current?.maxRound ?? shownRound,
          drivers: orderStandings((dResult.standings ?? []).map((d, i) => mapDriverStanding(d, i, previous))),
          teams: orderStandings((tResult.standings ?? []).map(mapConstructorStanding)),
        }));
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('[Standings]', err);
          setFailed(true);
        }
      }
    })();

    return () => ctrl.abort();
  }, [year, round]);

  // Derived rather than stored: a `setLoading(true)` at the top of the effect
  // would queue a second render before the first has painted.
  const loading = data === null && !failed;
  const stale = data !== null && data.requestedRound !== round;

  if (loading) {
    return (
      <div className="mt-4 space-y-3">
        <Skeleton className="h-10 w-48" rounded="rounded-xl" />
        <Skeleton className="h-14 w-full" rounded="rounded-xl" />
        <Skeleton className="h-14 w-full" rounded="rounded-xl" />
        <Skeleton className="h-14 w-full" rounded="rounded-xl" />
        <Skeleton className="h-14 w-full" rounded="rounded-xl" />
        <Skeleton className="h-14 w-full" rounded="rounded-xl" />
      </div>
    );
  }

  if (failed || !data) {
    return (
      <div className="mt-4 rounded-2xl bg-bg-card px-4 py-8 text-center text-sm text-ink-secondary ring-1 ring-stroke">
        Standings couldn&apos;t be loaded for {year}.
      </div>
    );
  }

  const rows = mode === 'drivers' ? data.drivers : data.teams;

  const toggle = (id) => setExpanded((current) => (current === id ? null : id));
  const switchMode = (next) => {
    setMode(next);
    setExpanded(null); // an open driver row means nothing in the teams table
  };

  return (
    <div className="mt-4 animate-fade-in">
      <div className="mb-3 flex items-center gap-1 rounded-xl bg-bg-card p-1 ring-1 ring-stroke">
        <button
          onClick={() => switchMode('drivers')}
          className={`flex-1 rounded-lg px-3 py-2 text-xs font-bold transition-all ${
            mode === 'drivers'
              ? 'bg-f1-red text-white shadow-glow-red-soft'
              : 'text-ink-secondary hover:text-ink-primary'
          }`}
        >
          Drivers
        </button>
        <button
          onClick={() => switchMode('teams')}
          className={`flex-1 rounded-lg px-3 py-2 text-xs font-bold transition-all ${
            mode === 'teams'
              ? 'bg-f1-red text-white shadow-glow-red-soft'
              : 'text-ink-secondary hover:text-ink-primary'
          }`}
        >
          Constructors
        </button>
      </div>

      <RoundPicker
        year={year}
        value={round}
        maxRound={data.maxRound}
        onChange={(next) => {
          setRound(next);
          setExpanded(null);
        }}
      />

      <div className={stale ? 'opacity-50 transition-opacity' : 'transition-opacity'}>
        {rows.length === 0 ? (
          <div className="rounded-2xl bg-bg-card px-4 py-8 text-center text-sm text-ink-secondary ring-1 ring-stroke">
            No standings data available for this round.
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((row) =>
              mode === 'drivers' ? (
                <DriverRow
                  key={row.driver.id}
                  row={row}
                  maxPoints={rows[0]?.points || 1}
                  season={year}
                  expanded={expanded === row.driver.id}
                  onToggle={() => toggle(row.driver.id)}
                />
              ) : (
                <TeamRow
                  key={row.team.id}
                  row={row}
                  maxPoints={rows[0]?.points || 1}
                  season={year}
                  expanded={expanded === row.team.id}
                  onToggle={() => toggle(row.team.id)}
                />
              )
            )}
          </div>
        )}
      </div>

      <p className="mt-4 text-center text-[10px] uppercase tracking-widest-plus text-ink-muted">
        {year} season{data.shownRound ? ` · after round ${data.shownRound}` : ''} · sourced from Jolpica-F1
      </p>
    </div>
  );
}

/**
 * RoundPicker — "as it finished", or the table as it stood after any round.
 *
 * Label depends on whether the season is over: mid-season the last available
 * round is simply the latest race, not a final classification, and calling it
 * "final" would be wrong for most of the year.
 */
function RoundPicker({ year, value, maxRound, onChange }) {
  if (!maxRound || maxRound < 2) return null;

  const complete = year < new Date().getUTCFullYear();
  const latestLabel = complete ? `Final standings (R${maxRound})` : `Latest (after R${maxRound})`;

  return (
    <div className="mb-3 flex items-center gap-2">
      <label htmlFor="standings-round" className="text-[10px] font-bold uppercase tracking-widest-plus text-ink-tertiary">
        Show
      </label>
      <select
        id="standings-round"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : parseInt(e.target.value, 10))}
        className="pressable flex-1 appearance-none rounded-xl bg-bg-card px-3 py-2 pr-7 text-xs font-bold text-ink-primary ring-1 ring-stroke transition-all focus:ring-f1-red/60"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%23a1a1aa' stroke-width='2' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 8px center',
        }}
      >
        <option value="">{latestLabel}</option>
        {Array.from({ length: maxRound }, (_, i) => maxRound - i).map((r) => (
          <option key={r} value={r}>
            After round {r}
          </option>
        ))}
      </select>
    </div>
  );
}

function mapDriverStanding(d, i, previousPositions) {
  const constructor = d.Constructors?.[d.Constructors.length - 1];
  const driverId = d.Driver?.driverId ?? `driver-${i}`;
  const currentPos = parsePosition(d.position);
  const prevPos = previousPositions[driverId];
  let delta = null;
  // A delta is only meaningful when BOTH rounds ranked the driver.
  if (currentPos !== null && Number.isFinite(prevPos)) {
    delta = prevPos - currentPos; // positive = gained positions
  }
  return {
    driver: { id: driverId, name: driverDisplayName(d.Driver), abbr: d.Driver?.code ?? '' },
    team: { id: constructor?.constructorId ?? `team-${i}`, name: constructor?.name ?? 'Unknown' },
    position: currentPos,
    points: parseFloat(d.points) || 0,
    wins: parseInt(d.wins, 10) || 0,
    delta,
  };
}

function mapConstructorStanding(t, i) {
  return {
    team: { id: t.Constructor?.constructorId ?? `team-${i}`, name: t.Constructor?.name ?? 'Unknown' },
    position: parsePosition(t.position),
    points: parseFloat(t.points) || 0,
    wins: parseInt(t.wins, 10) || 0,
  };
}

function positionDeltaDisplay(delta) {
  if (delta === null) return { label: '—', color: 'text-ink-tertiary' };
  if (delta > 0) return { label: `▲${delta}`, color: 'text-signal-green' };
  if (delta < 0) return { label: `▼${Math.abs(delta)}`, color: 'text-f1-red' };
  return { label: '—', color: 'text-ink-tertiary' };
}

/** Shared shell: livery rail, points bar, position badge, expansion. */
function StandingsRow({ livery, position, points, maxPoints, expanded, onToggle, label, children, detail }) {
  const ranked = position !== null;

  return (
    <div className="relative overflow-hidden rounded-xl bg-bg-card ring-1 ring-stroke">
      <div
        aria-hidden="true"
        className="absolute left-0 top-0 h-full w-[3px]"
        style={{ backgroundColor: livery }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-y-0 left-0 bg-f1-red/8"
        style={{ width: `${Math.max((points / maxPoints) * 100, 2)}%` }}
      />

      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={`${label} — show race-by-race results`}
        className="pressable relative flex w-full items-center gap-3 px-3 py-3 pl-[18px] text-left"
      >
        <div
          title={ranked ? undefined : 'Not classified — scored no points this season'}
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg font-mono text-sm font-bold tabular-nums ${
            ranked && position <= 3
              ? positionStyle(position)
              : 'bg-bg-card2 text-ink-secondary ring-1 ring-stroke'
          } ${ranked ? '' : 'text-ink-muted'}`}
        >
          {ranked ? position : '—'}
        </div>

        {children}

        <Icon
          name={expanded ? 'chevronUp' : 'chevronDown'}
          size={14}
          className="shrink-0 text-ink-muted"
        />
      </button>

      {expanded ? (
        <div className="relative border-t border-stroke bg-bg-base/40">{detail}</div>
      ) : null}
    </div>
  );
}

function DriverRow({ row, maxPoints, season, expanded, onToggle }) {
  const livery = teamColors[row.team.name] ?? '#666';
  const delta = positionDeltaDisplay(row.delta);

  return (
    <StandingsRow
      livery={livery}
      position={row.position}
      points={row.points}
      maxPoints={maxPoints}
      expanded={expanded}
      onToggle={onToggle}
      label={row.driver.name}
      detail={
        <StandingsHistory season={season} kind="driver" id={row.driver.id} accent={livery} />
      }
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-ink-primary">{row.driver.name}</span>
          <span className="font-mono text-2xs text-ink-tertiary">{row.driver.abbr}</span>
        </div>
        <div className="truncate text-2xs uppercase tracking-widest-plus text-ink-tertiary">
          {row.team.name}
        </div>
      </div>

      <div className="shrink-0 text-right">
        <div className="font-mono text-sm font-bold tabular-nums text-ink-primary">{row.points}</div>
        <div className="text-2xs text-ink-tertiary">
          {row.wins > 0 ? `${row.wins} win${row.wins > 1 ? 's' : ''}` : '-'}
        </div>
      </div>

      <div
        className={`ml-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-bg-card2 font-mono text-xs font-bold tabular-nums ${delta.color}`}
      >
        {delta.label}
      </div>
    </StandingsRow>
  );
}

function TeamRow({ row, maxPoints, season, expanded, onToggle }) {
  const livery = teamColors[row.team.name] ?? '#666';

  return (
    <StandingsRow
      livery={livery}
      position={row.position}
      points={row.points}
      maxPoints={maxPoints}
      expanded={expanded}
      onToggle={onToggle}
      label={row.team.name}
      detail={
        <StandingsHistory season={season} kind="constructor" id={row.team.id} accent={livery} />
      }
    >
      <div className="min-w-0 flex-1">
        <span className="truncate text-sm font-semibold text-ink-primary">{row.team.name}</span>
      </div>

      {/* Wins were drivers-only until now — the same number is just as
          interesting for a team, and the API has always returned it. */}
      <div className="shrink-0 text-right">
        <div className="font-mono text-sm font-bold tabular-nums text-ink-primary">{row.points}</div>
        <div className="text-2xs text-ink-tertiary">
          {row.wins > 0 ? `${row.wins} win${row.wins > 1 ? 's' : ''}` : '-'}
        </div>
      </div>
    </StandingsRow>
  );
}

function positionStyle(pos) {
  if (pos === 1) return 'bg-yellow-500/15 text-yellow-400 ring-1 ring-yellow-500/60';
  if (pos === 2) return 'bg-zinc-400/15 text-zinc-200 ring-1 ring-zinc-400/60';
  if (pos === 3) return 'bg-amber-700/15 text-amber-500 ring-1 ring-amber-700/60';
  return '';
}
